import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import FabBar from "@/design/components/FabBar";
import MasterListVirtualized from "@/design/components/MasterListVirtualized";
import { disableSearch, enableSearch } from "@/store/reducers/ui";
import useSearch from "@/utils/hooks/useSearch";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viNumpad, viPlus } from "@sippy-platform/valkyrie";

import { useMediaQuery, useTheme } from "@mui/material";
import Button from "@mui/material/Button";

function IvrList() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const matchesMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const ivrStore = useSelector((state) => state.domain.ivrs);
  const searchValue = useSelector((state) => state.ui.searchValue);
  const progressBar = useSelector((state) => state.ui.progressBar);

  // Search every time ivrList or searchQuery changes
  const searchResults = useSearch(ivrStore.list, searchValue, ["name", "exten"]);

  useEffect(() => {
    toggleLoadingState(true);
    Promise.resolve(Api.getIvrs()).finally(toggleLoadingState);
  }, []);

  // Open first item or new if the viewport is large enough
  useEffect(() => {
    if (!id && matchesMdUp && ivrStore.meta.lastFetched) {
      if (ivrStore.list.length >= 1) {
        navigate(`${ivrStore.list[0].id}`);
      } else {
        navigate("new");
      }
    }
  }, [id, matchesMdUp, navigate, ivrStore.list, ivrStore.meta.lastFetched]);

  useEffect(() => {
    dispatch(enableSearch());
  }, [dispatch]);
  useEffect(() => () => dispatch(disableSearch()), [dispatch]);

  return (
    <>
      <MasterListVirtualized
        fabBar
        title="IVRs"
        aria-label="IVR list"
        id="ivr-list"
        list={searchResults}
        listItemProps={{
          primary: (x) => x.name,
          secondary: (x) => x.exten,
          to: (x) => `../${x.id}`,
          selected: (x) => x.id === Number(id),
          icon: viNumpad,
        }}
        hideContentLoader={ivrStore.meta.count >= 1 && searchResults.length >= 1}
        contentLoaderProps={{
          count: ivrStore.meta.count,
          loading: progressBar,
          noItems: "You don't have any IVRs yet",
          noResults: "No IVRs in your current query",
          fetching: "Getting your IVRs",
          icon: viNumpad,
        }}
      />
      <FabBar>
        <Button variant="contained" component={NavLink} to="new" startIcon={<Valkyrie icon={viPlus} />}>
          Add
        </Button>
      </FabBar>
    </>
  );
}

export default IvrList;
